/**
 * Client Entry Point — Browser Hydration
 *
 * Hydrates the server-rendered HTML with React, attaching event handlers
 * and enabling client-side navigation via React Router.
 *
 * This is the esbuild entry point for the client bundle.
 * All route-level lazy() imports become code-split chunks.
 *
 * The client route tree (routes.client.gen.tsx) contains ONLY UI components —
 * no loaders, no Connect-RPC, no protobuf, no process.env. Data fetching
 * for client-side navigation is handled by the dataStrategy, which fetches
 * from the /_data BFF endpoint on the Hono server.
 */
import '../styles.gen.css';

import { mergeDeliveredFlags, type EvaluatedWebFlags } from '@letsgethome/lib/flags/evaluate';
import { parseEnvSlug } from '@letsgethome/lib/flags/registry_public_gen';
import { plaintextString } from '@letsgethome/types/branded';
import { StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { RouterProvider, type HydrationState } from 'react-router';

import { AppErrorBoundary } from '../shared/components/error-boundary';
import { clientLogger } from '../shared/lib/client-logger';
import { installMapsAuthFailureHandler } from '../shared/lib/maps-health';
import { reportHydration } from '../shared/lib/metrics/hydration';
import type { RuntimeConfig } from '../shared/providers/ConfigProvider';
import { bffDataStrategy } from './bff-data-strategy';
import { createHydratedRouter } from './create-hydrated-router';
import { HtmlDocument, type ShellManifest } from './HtmlDocument';
import { reportRecoverableError } from './hydration-recoverable-error';
import { localizedRoutes } from './routes.client.gen';
import { ShellProviders } from './ShellProviders';

declare global {
  interface Window {
    // Optional: SSR injects these, but they may be absent (e.g. a hydration
    // before the bootstrap script ran) — the `?? fallback` reads stay honest.
    __LOCALE__?: string;
    __MESSAGES__?: Record<string, string>;
    __CONFIG__: RuntimeConfig;
    __FLAGS__?: EvaluatedWebFlags;
    __FLAGS_ENV__?: string;
    __TRACE_ID__?: string;
    __staticRouterHydrationData?: HydrationState;
    // Build-derived head inputs the server serialized so the client can render
    // a `<head>` identical to the server's (see server/bootstrap.ts).
    __SHELL__?: ShellManifest;
  }
}

// Global browser-error capture. Registered before hydration so an uncaught
// exception or unhandled rejection from ANY script (incl. third-party like
// Google Maps) reaches Loki via the existing clientLogger beacon — previously
// there was no window.onerror/unhandledrejection handler at all, so those were
// lost. gm_authFailure is installed here too, the earliest point, so Google's
// auth-failure callback has a handler before the Maps SDK loads.
//
// What does NOT arrive here any more: a hydration mismatch React RECOVERED from.
// React's default `onRecoverableError` is `reportError`, a synthetic `error`
// event this handler logged as an anonymous "Uncaught error"; `hydrateRoot`
// below declares its own (LET-122), so those are reported by the component that
// produced them, at `warn`, and never reach this listener.
window.addEventListener('error', (event) => {
  clientLogger.error(
    {
      component: 'window',
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
    },
    'Uncaught error'
  );
});
window.addEventListener('unhandledrejection', (event) => {
  clientLogger.error(
    { component: 'window', reason: String(event.reason) },
    'Unhandled promise rejection'
  );
});
installMapsAuthFailureHandler();

const locale = window.__LOCALE__ ?? 'zh-HK';
const messages = window.__MESSAGES__ ?? {};
const runtimeConfig = window.__CONFIG__;
// PER-KEY merge onto the compiled floor — the SAME floor env the server
// rendered with (serialized as __FLAGS_ENV__ from server/flags.ts
// floorEnv), so dev hydration floors dev and deployed hydration floors
// prod; a missing/unknown value degrades to the conservative prod floor.
// Same per-key skew story: cached HTML from an older server that lacks a
// newly-registered key degrades that key alone, never an `undefined`
// typed as boolean.
const flags: EvaluatedWebFlags = mergeDeliveredFlags(
  parseEnvSlug(window.__FLAGS_ENV__ ?? '') ?? 'prod',
  new Map(Object.entries(window.__FLAGS__ ?? {}))
);

// Every client-side load is a `/_data` fetch for the URL being loaded — there
// is no hydration-data stash in front of it (#7816; the reason, and the
// measurement, are in `bff-data-strategy.ts`).
const router = await createHydratedRouter(localizedRoutes, {
  hydrationData: window.__staticRouterHydrationData,
  dataStrategy: bffDataStrategy,
});

const hydrationStart = performance.now();

// Last-resort hydration-failure copy. Intentionally hardcoded (not via i18n):
// this renders when the React tree — including the IntlProvider — failed to
// mount, so no translation context is available. Held as a constant so it sits
// outside JSX-text position (no i18next/no-literal-string, no disable needed).
const HYDRATION_ERROR_MESSAGE = plaintextString('Something went wrong.');

// The shell manifest the server serialized (css + modulepreloads + locale) so
// the client renders a `<head>` identical to the server's. Falls back to a
// bare shell if absent (a pre-bootstrap hydration) — the ?? reads stay honest.
const shell: ShellManifest = window.__SHELL__ ?? { locale, cssFiles: [], preloadChunks: [] };

// Hydrate the WHOLE document with the SAME `HtmlDocument`/`ShellProviders` the
// server rendered (the SSOT) — React owns <head>, so SeoHead/Image metadata
// hoists symmetrically and cannot duplicate. `AppErrorBoundary` + `StrictMode`
// are DOM-transparent, so they don't perturb the hydrated structure.
//
// `onRecoverableError` is REQUIRED here (LET-122, `@letsgethome/require-hydrate-
// root-recoverable-error`): a hydration mismatch React recovers from is handed
// to this option WITH its component stack; without the option React's default
// is `reportError`, which lands in the `window` handler above as an anonymous
// `Uncaught error` naming no component — 12 such lines per 3 days in prod, none
// of them attributable. The reporter says what kind of mismatch it was, in
// which component, at the severity a RECOVERED condition deserves.
hydrateRoot(
  document,
  <StrictMode>
    <HtmlDocument
      locale={shell.locale}
      cssFiles={shell.cssFiles}
      preloadChunks={shell.preloadChunks}
    >
      <ShellProviders locale={locale} messages={messages} config={runtimeConfig} flags={flags}>
        <AppErrorBoundary fallback={<p>{HYDRATION_ERROR_MESSAGE}</p>}>
          <RouterProvider router={router} />
        </AppErrorBoundary>
      </ShellProviders>
    </HtmlDocument>
  </StrictMode>,
  { onRecoverableError: reportRecoverableError }
);

reportHydration(hydrationStart);
void import('../shared/lib/metrics/web-vitals').then((m) => m.initWebVitals());
